import { useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle, XCircle } from "lucide-react";

export default function AuthCallback() {
  const [, setLocation] = useLocation();
  const searchParams = new URLSearchParams(useSearch());
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('正在处理授权...');

  const forumCallback = trpc.auth.forumCallback.useMutation({
    onSuccess: (data) => {
      // Session is already created on server side via cookie
      // Store user info in localStorage for quick access
      localStorage.setItem('forum_user', JSON.stringify(data.user));
      
      setStatus('success');
      setMessage('授权成功！正在跳转...');
      
      // Redirect to home after 1 second
      setTimeout(() => {
        setLocation('/');
        // Reload to refresh auth state
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      setStatus('error');
      setMessage(`授权失败：${error.message}`);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        setLocation('/login');
      }, 3000);
    },
  });

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    // Check for errors from OAuth provider
    if (error) {
      setStatus('error');
      setMessage(`授权失败：${error === 'access_denied' ? '用户拒绝授权' : error}`);
      setTimeout(() => setLocation('/login'), 3000);
      return;
    }

    // Validate required parameters
    if (!code || !state) {
      setStatus('error');
      setMessage('授权参数缺失');
      setTimeout(() => setLocation('/login'), 3000);
      return;
    }

    // Verify state to prevent CSRF
    const savedState = localStorage.getItem('forum_oauth_state');
    if (savedState !== state) {
      setStatus('error');
      setMessage('状态验证失败，可能存在安全风险');
      setTimeout(() => setLocation('/login'), 3000);
      return;
    }

    // Clear saved state
    localStorage.removeItem('forum_oauth_state');

    // Exchange code for tokens
    forumCallback.mutate({ code, state });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <Card className="w-full max-w-md glass border-primary/20">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl flex items-center justify-center gap-2">
            {status === 'loading' && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
            {status === 'success' && <CheckCircle className="h-6 w-6 text-green-500" />}
            {status === 'error' && <XCircle className="h-6 w-6 text-red-500" />}
            {status === 'loading' && '处理中'}
            {status === 'success' && '授权成功'}
            {status === 'error' && '授权失败'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground">{message}</p>
        </CardContent>
      </Card>
    </div>
  );
}
